import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface MobileDrawerProps {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    side?: 'left' | 'right';
    title?: string;
    width?: string;
    className?: string;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({
    open,
    onClose,
    children,
    side = 'left',
    title,
    width = 'w-[225px]',
    className = '',
}) => {
    // Lock body scroll when open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    const slideFrom = side === 'left' ? { x: '-100%' } : { x: '100%' };

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    {/* Backdrop — premium blur */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-navy-900/35 dark:bg-black/55"
                        style={{
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        }}
                        onClick={onClose}
                    />

                    {/* Drawer panel */}
                    <motion.div
                        initial={slideFrom}
                        animate={{ x: 0 }}
                        exit={slideFrom}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className={[
                            'absolute top-0 bottom-0',
                            side === 'left' ? 'left-0' : 'right-0',
                            width,
                            'flex flex-col overflow-hidden bg-[var(--drawer-bg,#ffffff)] shadow-[4px_0_40px_-4px_rgba(0,0,0,0.12)]',
                            className,
                        ].join(' ')}
                    >
                        {/* Branded accent strip */}
                        <div
                            className="h-[3px] w-full flex-shrink-0 bg-gradient-to-r from-brand-500 via-brand-400 via-70% to-brand-500 bg-[length:200%_100%] animate-shimmer"
                        />

                        {/* Header */}
                        {(title || true) && (
                            <div className="flex items-center justify-between px-5 py-3.5 border-b border-surface-200/60 dark:border-surface-dark-200">
                                {title && (
                                    <h3 className="text-body-sm font-bold text-navy-500 dark:text-slate-100">{title}</h3>
                                )}
                                <button
                                    onClick={onClose}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-surface-100 dark:hover:text-slate-300 dark:hover:bg-surface-dark-200 transition-colors ml-auto"
                                    aria-label="Close"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 flex flex-col overflow-y-auto bg-white dark:bg-surface-dark-50">{children}</div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default MobileDrawer;
